import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Building2, MapPin, Users, Calendar, Check, ArrowRight, Loader2 } from 'lucide-react';

const australianStates = [
  { code: 'QLD', name: 'Queensland' },
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'SA', name: 'South Australia' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'NT', name: 'Northern Territory' },
  { code: 'ACT', name: 'Australian Capital Territory' }
];

const sectorOptions = [
  'Agriculture, Forestry and Fishing',
  'Mining',
  'Manufacturing',
  'Electricity, Gas, Water and Waste Services',
  'Construction',
  'Wholesale Trade',
  'Retail Trade',
  'Accommodation and Food Services',
  'Transport, Postal and Warehousing',
  'Information Media and Telecommunications',
  'Financial and Insurance Services',
  'Rental, Hiring and Real Estate Services',
  'Professional, Scientific and Technical Services',
  'Administrative and Support Services',
  'Public Administration and Safety',
  'Education and Training',
  'Health Care and Social Assistance',
  'Arts and Recreation Services',
  'Other Services'
];

// State-based electricity emission factors (kg CO2-e per kWh)
// Using NGA Factors 2025 (latest available)
const NGA_FACTOR_YEAR = 2025;
const STATE_ELECTRICITY_FACTORS = {
  'QLD': 0.67,
  'NSW': 0.64,
  'ACT': 0.64,
  'VIC': 0.82,
  'SA': 0.25,
  'WA': 0.51,
  'TAS': 0.15,
  'NT': 0.54
};

const SetupWizardPage = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [userId, setUserId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    abn: '',
    state: '',
    sector: '',
    employee_count: '',
    reporting_year: new Date().getFullYear()
  });

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      // Get current auth user
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        navigate('/login');
        return;
      }

      // Get user's internal ID
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();

      if (!userData) {
        navigate('/login');
        return;
      }
      setUserId(userData.id);

      // Get organisations
      const { data: orgs } = await supabase
        .from('organisations')
        .select('*')
        .eq('owner_id', userData.id);

      if (orgs && orgs.length > 0) {
        const primaryOrg = orgs.find(o => o.is_primary) || orgs[0];
        setCurrentOrg(primaryOrg);
        
        // If setup is already complete, redirect to dashboard
        if (primaryOrg.setup_complete) {
          navigate('/dashboard');
          return;
        }
        
        // Pre-fill form with existing data
        setFormData({
          name: primaryOrg.name || '',
          abn: primaryOrg.abn || '',
          state: primaryOrg.state || '',
          sector: primaryOrg.sector || '',
          employee_count: primaryOrg.employee_count || '',
          reporting_year: primaryOrg.reporting_year || new Date().getFullYear()
        });
      }
    } catch (error) {
      console.error('Error checking setup status:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const recalculateElectricityEmissions = async (orgId, newState) => {
    // Get all electricity emissions for this org
    const { data: electricityEmissions } = await supabase
      .from('emissions_data')
      .select('id, quantity')
      .eq('organisation_id', orgId)
      .eq('category', 'electricity');

    if (!electricityEmissions || electricityEmissions.length === 0) return;

    const newFactor = STATE_ELECTRICITY_FACTORS[newState] || 0.64;

    // Update each electricity emission with the new factor
    for (const emission of electricityEmissions) {
      const newCo2 = (emission.quantity * newFactor) / 1000; // Convert to tonnes
      await supabase
        .from('emissions_data')
        .update({ co2_equivalent: newCo2 })
        .eq('id', emission.id);
    }
  };

  const handleSaveStep = async () => {
    if (!currentOrg) return;
    
    setSaving(true);
    try {
      const oldState = currentOrg.state;
      
      // Validate ABN if provided
      let cleanAbn = null;
      if (formData.abn) {
        cleanAbn = formData.abn.replace(/\s/g, '').replace(/-/g, '');
        if (cleanAbn.length !== 11 || !/^\d+$/.test(cleanAbn)) {
          toast.error('ABN must be 11 digits');
          setSaving(false);
          return;
        }
      }

      // Update organisation
      const updateData = {
        name: formData.name,
        abn: cleanAbn,
        state: formData.state ? formData.state.toUpperCase() : null,
        sector: formData.sector || null,
        employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
        reporting_year: formData.reporting_year
      };

      const { error: updateError } = await supabase
        .from('organisations')
        .update(updateData)
        .eq('id', currentOrg.id);

      if (updateError) {
        console.error('Error updating org:', updateError);
        toast.error('Failed to save. Please try again.');
        setSaving(false);
        return;
      }

      // If state changed, recalculate electricity emissions
      if (oldState !== formData.state && formData.state) {
        await recalculateElectricityEmissions(currentOrg.id, formData.state);
      }

      // Update local state
      setCurrentOrg({ ...currentOrg, ...updateData });

      if (step < 3) {
        setStep(step + 1);
        toast.success('Progress saved!');
      } else {
        // Complete setup - mark setup_complete = true
        const { error: completeError } = await supabase
          .from('organisations')
          .update({ setup_complete: true })
          .eq('id', currentOrg.id);

        if (completeError) {
          console.error('Error completing setup:', completeError);
          toast.error('Failed to complete setup.');
          setSaving(false);
          return;
        }

        toast.success('Setup complete! Welcome to Lucent Sky ESG.');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.name.trim().length > 2;
      case 2:
        return formData.state !== '';
      case 3:
        return true; // Optional fields
      default:
        return true;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#003B5C]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="setup-wizard-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#003B5C] to-[#74A57F] rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Lucent Sky ESG
            </span>
          </div>
          <Button variant="ghost" onClick={signOut} className="text-slate-500">
            Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Progress Indicator */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                  step >= s 
                    ? 'bg-[#003B5C] text-white' 
                    : 'bg-slate-200 text-slate-500'
                }`}>
                  {step > s ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-24 md:w-32 h-1 mx-2 rounded transition-all ${
                    step > s ? 'bg-[#74A57F]' : 'bg-slate-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-sm text-slate-500">
            <span>Organisation</span>
            <span>Location</span>
            <span>Details</span>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
          {step === 1 && (
            <div>
              <h1 className="text-2xl font-bold text-[#003B5C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Welcome! Let's set up your organisation
              </h1>
              <p className="text-slate-500 mb-8">
                This information will be used in your emissions reports and compliance documents.
              </p>
              
              <div className="space-y-6">
                <div>
                  <Label className="text-[#003B5C] font-medium">Organisation Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your company or organisation name"
                    className="mt-2 h-12"
                    data-testid="org-name-input"
                  />
                </div>
                <div>
                  <Label className="text-[#003B5C] font-medium">ABN (Optional)</Label>
                  <Input
                    value={formData.abn}
                    onChange={(e) => setFormData({ ...formData, abn: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                    placeholder="12 345 678 901"
                    className="mt-2 h-12"
                    data-testid="abn-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">11-digit Australian Business Number</p>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h1 className="text-2xl font-bold text-[#003B5C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Where are you located?
              </h1>
              <p className="text-slate-500 mb-8">
                Your state determines electricity emission factors and applicable compliance frameworks.
              </p>
              
              <div className="space-y-6">
                <div>
                  <Label className="text-[#003B5C] font-medium flex items-center">
                    <MapPin className="w-4 h-4 mr-2" />
                    State/Territory *
                  </Label>
                  <Select 
                    value={formData.state} 
                    onValueChange={(val) => setFormData({ ...formData, state: val })}
                  >
                    <SelectTrigger className="mt-2 h-12" data-testid="state-select">
                      <SelectValue placeholder="Select your state or territory" />
                    </SelectTrigger>
                    <SelectContent>
                      {australianStates.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.state && (
                  <div className="bg-[#F8F9FA] rounded-xl p-4 border border-slate-200">
                    <p className="text-sm text-slate-600">
                      <span className="font-semibold text-[#003B5C]">
                        {australianStates.find(s => s.code === formData.state)?.name}
                      </span>
                      {' '}uses electricity factor:{' '}
                      <span className="font-mono text-[#74A57F] font-semibold">
                        {STATE_ELECTRICITY_FACTORS[formData.state]} kg CO₂-e/kWh
                      </span>
                      <span className="text-xs text-slate-400 ml-1">
                        (NGA Factors {NGA_FACTOR_YEAR})
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h1 className="text-2xl font-bold text-[#003B5C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Almost there! A few more details
              </h1>
              <p className="text-slate-500 mb-8">
                These help us benchmark your emissions and provide industry-specific insights.
              </p>
              
              <div className="space-y-6">
                <div>
                  <Label className="text-[#003B5C] font-medium">Industry Sector</Label>
                  <Select 
                    value={formData.sector} 
                    onValueChange={(val) => setFormData({ ...formData, sector: val })}
                  >
                    <SelectTrigger className="mt-2 h-12" data-testid="sector-select">
                      <SelectValue placeholder="Select your industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {sectorOptions.map((sector) => (
                        <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#003B5C] font-medium flex items-center">
                    <Users className="w-4 h-4 mr-2" />
                    Number of Employees
                  </Label>
                  <Input
                    type="number"
                    value={formData.employee_count}
                    onChange={(e) => setFormData({ ...formData, employee_count: parseInt(e.target.value) || '' })}
                    placeholder="e.g., 25"
                    className="mt-2 h-12"
                    data-testid="employee-count-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-10 pt-6 border-t border-slate-100">
            {step > 1 ? (
              <Button 
                variant="outline" 
                onClick={() => setStep(step - 1)}
                disabled={saving}
              >
                Back
              </Button>
            ) : (
              <div />
            )}
            <Button
              onClick={handleSaveStep}
              disabled={!isStepValid() || saving}
              className="bg-[#003B5C] text-white hover:bg-[#002A42] min-w-[140px]"
              data-testid="continue-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : step === 3 ? (
                <>
                  Complete Setup
                  <Check className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Skip option */}
        <div className="text-center mt-6">
          <button
            onClick={() => {
              toast.info('You can complete setup anytime from Settings');
              navigate('/dashboard');
            }}
            className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
          >
            Skip for now →
          </button>
        </div>
      </main>
    </div>
  );
};

export default SetupWizardPage;
